import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, RadialBarChart, RadialBar } from "recharts";

// ── Fake data ──
const equity = [
  { d:"05-09", v:0 },{ d:"05-11", v:120 },{ d:"05-13", v:80 },{ d:"05-15", v:-60 },
  { d:"05-17", v:-180 },{ d:"05-19", v:-320 },{ d:"05-21", v:-410 },{ d:"05-23", v:-280 },
  { d:"05-25", v:-190 },{ d:"05-27", v:-390 },{ d:"05-29", v:-180 },{ d:"06-01", v:200 },
  { d:"06-03", v:680 },{ d:"06-05", v:980 },{ d:"06-06", v:1305 },
];
const instruments = [
  { name:"MNQ",     pnl:3231, wr:60 },
  { name:"XAUUSD",  pnl:2495, wr:64 },
  { name:"EUR/USD", pnl:591,  wr:50 },
  { name:"AUDCAD",  pnl:-16,  wr:33 },
  { name:"GC",      pnl:-997, wr:28 },
];
const sessions = [
  { name:"New York", pnl:890,  trades:6 },
  { name:"London",   pnl:415,  trades:4 },
  { name:"Tokyo",    pnl:0,    trades:1 },
  { name:"Sydney",   pnl:0,    trades:1 },
];
const stats = { pnl:1305, wr:58, pf:2.08, rr:2.71, score:89, streak:3, trades:12, today:0, week:545, month:545 };
const clrGreen = "#22c55e";
const clrRed   = "#ef4444";
const clrAmber = "#f59e0b";

// ── Shared mini components ──
const fmt = (n) => {
  if (Math.abs(n) >= 1000) return (n/1000).toFixed(1).replace(/\.0$/,"") + "k";
  return String(Math.round(n));
};

// ──────────────────────────────────────────────
// DIRECTION 1 — TERMINAL (v2)
// ──────────────────────────────────────────────
function Terminal() {
  const G = "#00ff41";
  const mono = "'Courier New', 'Lucida Console', monospace";
  const border = "1px solid rgba(0,255,65,0.15)";
  const dim = "rgba(0,255,65,0.4)";
  const maxPnl = Math.max(...instruments.map(i => Math.abs(i.pnl)));

  const Block = ({ children, style }) => (
    <div style={{ border, padding:"14px 16px", position:"relative", ...style }}>{children}</div>
  );
  const Label = ({ children }) => (
    <div style={{ fontSize:9, color:dim, letterSpacing:"0.25em", marginBottom:8, fontFamily:mono }}>{children}</div>
  );

  return (
    <div style={{ background:"#000", minHeight:"100vh", fontFamily:mono, color:G, display:"flex", flexDirection:"column" }}>

      {/* ── TOP BAR ── */}
      <div style={{ padding:"10px 20px", borderBottom:border, display:"flex", justifyContent:"space-between", alignItems:"center", background:"rgba(0,255,65,0.03)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:20 }}>
          <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.3em", textShadow:`0 0 10px ${G}` }}>FYLTRA</span>
          <span style={{ fontSize:9, color:dim, letterSpacing:"0.15em" }}>TRADING TERMINAL v2.1</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16, fontSize:9, color:dim }}>
          <span>SAT 2026-06-07</span>
          <span style={{ color:G }}>◉ LIVE</span>
          <span>DANY</span>
        </div>
      </div>

      {/* ── MAIN GRID ── */}
      <div style={{ flex:1, display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gridTemplateRows:"auto 1fr auto", gap:0 }}>

        {/* P&L HERO — 2 cols wide */}
        <div style={{ gridColumn:"1/3", borderRight:border, borderBottom:border, padding:"24px 24px 20px", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, background:`radial-gradient(ellipse 70% 80% at 20% 50%, rgba(0,255,65,0.06) 0%, transparent 65%)`, pointerEvents:"none" }}/>
          <Label>TOTAL P&L</Label>
          <div style={{ fontSize:80, fontWeight:700, lineHeight:0.9, letterSpacing:"-0.03em", color:G, textShadow:`0 0 40px rgba(0,255,65,0.7), 0 0 80px rgba(0,255,65,0.3)` }}>
            +{fmt(stats.pnl)}€
          </div>
          <div style={{ marginTop:16, display:"flex", gap:24, fontSize:10, color:dim, letterSpacing:"0.1em" }}>
            <span>{stats.trades} TRADES</span>
            <span style={{ color:"rgba(0,255,65,0.25)" }}>///</span>
            <span>AUJ +{stats.today}€</span>
            <span style={{ color:"rgba(0,255,65,0.25)" }}>///</span>
            <span>SEM +{fmt(stats.week)}€</span>
            <span style={{ color:"rgba(0,255,65,0.25)" }}>///</span>
            <span>MOIS +{fmt(stats.month)}€</span>
          </div>
        </div>

        {/* WIN RATE */}
        <div style={{ borderRight:border, borderBottom:border, padding:"24px 20px" }}>
          <Label>WIN RATE</Label>
          <div style={{ fontSize:52, fontWeight:700, lineHeight:1, color:G }}>{stats.wr}%</div>
          <div style={{ margin:"12px 0 6px", height:2, background:"rgba(0,255,65,0.1)" }}>
            <div style={{ height:"100%", width:`${stats.wr}%`, background:G, boxShadow:`0 0 8px ${G}` }} />
          </div>
          <div style={{ fontSize:9, color:dim }}>7W &nbsp;·&nbsp; 5L</div>
        </div>

        {/* PF + RR */}
        <div style={{ borderBottom:border, padding:"24px 20px" }}>
          <div style={{ marginBottom:20 }}>
            <Label>PROFIT FACTOR</Label>
            <div style={{ fontSize:36, fontWeight:700, color:G }}>{stats.pf}</div>
          </div>
          <div>
            <Label>AVG R/R</Label>
            <div style={{ fontSize:36, fontWeight:700, color:G }}>{stats.rr}</div>
          </div>
        </div>

        {/* EQUITY CURVE — 2 cols */}
        <div style={{ gridColumn:"1/3", borderRight:border, borderBottom:border, padding:"16px 20px" }}>
          <Label>EQUITY CURVE — 30D</Label>
          <ResponsiveContainer width="100%" height={130}>
            <AreaChart data={equity} margin={{ top:4, right:0, left:-32, bottom:0 }}>
              <defs>
                <linearGradient id="tg1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={G} stopOpacity={0.2}/>
                  <stop offset="95%" stopColor={G} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="d" tick={{ fontSize:8, fill:dim, fontFamily:mono }} tickLine={false} axisLine={false} interval={4} />
              <YAxis tick={{ fontSize:8, fill:dim, fontFamily:mono }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background:"#000", border:`1px solid rgba(0,255,65,0.3)`, borderRadius:0, fontSize:10, fontFamily:mono, color:G }} />
              <Area type="monotone" dataKey="v" stroke={G} strokeWidth={1.5} fill="url(#tg1)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* BY INSTRUMENT — 2 cols */}
        <div style={{ gridColumn:"3/5", borderBottom:border, padding:"16px 20px" }}>
          <Label>BY INSTRUMENT</Label>
          {instruments.map(ins => (
            <div key={ins.name} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:9 }}>
              <span style={{ width:58, fontSize:10, color:"rgba(0,255,65,0.6)", letterSpacing:"0.06em" }}>{ins.name}</span>
              <div style={{ flex:1, height:2, background:"rgba(0,255,65,0.08)" }}>
                <div style={{ height:"100%", width:`${Math.abs(ins.pnl)/maxPnl*100}%`, background:ins.pnl>=0?G:clrRed, boxShadow:ins.pnl>=0?`0 0 6px ${G}`:undefined }} />
              </div>
              <span style={{ width:52, fontSize:10, textAlign:"right", color:ins.pnl>=0?G:clrRed, fontWeight:700 }}>{ins.pnl>=0?"+":""}{fmt(ins.pnl)}€</span>
              <span style={{ width:30, fontSize:9, color:dim, textAlign:"right" }}>{ins.wr}%W</span>
            </div>
          ))}
        </div>

        {/* SESSIONS */}
        <div style={{ gridColumn:"1/3", borderRight:border, padding:"16px 20px" }}>
          <Label>SESSIONS</Label>
          <div style={{ display:"flex", gap:24 }}>
            {sessions.map(s => (
              <div key={s.name}>
                <div style={{ fontSize:8, color:dim, letterSpacing:"0.12em", marginBottom:4 }}>{s.name.toUpperCase()}</div>
                <div style={{ fontSize:20, color:s.pnl>0?G:s.pnl<0?clrRed:dim, fontWeight:700 }}>{s.pnl>=0?"+":""}{fmt(s.pnl)}€</div>
                <div style={{ fontSize:8, color:dim, marginTop:2 }}>{s.trades}T</div>
              </div>
            ))}
          </div>
        </div>

        {/* SCORE */}
        <div style={{ gridColumn:"3/5", padding:"16px 20px" }}>
          <Label>SCORE FYLTRA</Label>
          <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
            <span style={{ fontSize:64, fontWeight:700, lineHeight:1, color:G, textShadow:`0 0 30px rgba(0,255,65,0.6)` }}>{stats.score}</span>
            <span style={{ fontSize:14, color:dim }}>/100</span>
          </div>
          <div style={{ marginTop:6, height:2, background:"rgba(0,255,65,0.1)", width:"100%" }}>
            <div style={{ height:"100%", width:`${stats.score}%`, background:G, boxShadow:`0 0 8px ${G}` }} />
          </div>
          <div style={{ marginTop:8, display:"flex", gap:20, fontSize:9, color:dim, letterSpacing:"0.1em" }}>
            <span>WR {stats.wr}%</span>
            <span>PF {stats.pf}</span>
            <span>R/R {stats.rr}</span>
            <span>STREAK {stats.streak}W</span>
          </div>
        </div>

      </div>
    </div>
  );
}


// ──────────────────────────────────────────────
// DIRECTION 2 — HERO GRAPHIQUE
// ──────────────────────────────────────────────
function HeroGraph() {
  const ff = "'Josefin Sans', sans-serif";
  const hc = clrGreen;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const v = payload[0].value;
    return (
      <div style={{ background:"rgba(10,10,10,0.9)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"8px 12px", fontSize:11, fontFamily:ff, color:"#fff" }}>
        {v >= 0 ? "+" : ""}{v}€
      </div>
    );
  };

  return (
    <div style={{ background:"#0a0a0a", minHeight:"100vh", padding:0, fontFamily:ff, color:"#fff", overflow:"hidden" }}>

      {/* HERO — graphique plein écran */}
      <div style={{ position:"relative", height:380 }}>
        {/* gradient overlay text */}
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0.1) 40%, rgba(10,10,10,0.8) 100%)", zIndex:2, pointerEvents:"none" }} />

        {/* chart */}
        <div style={{ position:"absolute", inset:0, zIndex:1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={equity} margin={{ top:0, right:0, left:0, bottom:0 }}>
              <defs>
                <linearGradient id="hg2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={hc} stopOpacity={0.35}/>
                  <stop offset="100%" stopColor={hc} stopOpacity={0.03}/>
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={hc} strokeWidth={2} fill="url(#hg2)" dot={false} />
              <Tooltip content={<CustomTooltip />} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Overlay content */}
        <div style={{ position:"absolute", inset:0, zIndex:3, display:"flex", flexDirection:"column", justifyContent:"space-between", padding:"28px 32px" }}>
          {/* top */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:4 }}>Bonjour, Dany</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.25)", letterSpacing:"0.12em" }}>Samedi 6 juin · {stats.trades} trades</div>
            </div>
            <div style={{ display:"flex", gap:24, textAlign:"right" }}>
              {[{ l:"Semaine", v:stats.week }, { l:"Mois", v:stats.month }].map(m => (
                <div key={m.l}>
                  <div style={{ fontSize:8, color:"rgba(255,255,255,0.35)", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:3 }}>{m.l}</div>
                  <div style={{ fontSize:18, color:m.v>=0?clrGreen:clrRed, fontWeight:700 }}>{m.v>=0?"+":""}{fmt(m.v)}€</div>
                </div>
              ))}
            </div>
          </div>

          {/* center — big P&L */}
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", letterSpacing:"0.25em", textTransform:"uppercase", marginBottom:6 }}>Total P&L</div>
            <div style={{ fontSize:80, fontWeight:800, color:hc, lineHeight:1, letterSpacing:"-0.03em", textShadow:`0 0 80px ${hc}55, 0 0 30px ${hc}30` }}>
              +{fmt(stats.pnl)}€
            </div>
          </div>

          {/* bottom — 4 inline stats */}
          <div style={{ display:"flex", justifyContent:"space-around", borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:16 }}>
            {[
              { l:"Win Rate", v:`${stats.wr}%`, c:stats.wr>=60?clrGreen:stats.wr>=45?clrAmber:clrRed },
              { l:"Profit Factor", v:String(stats.pf), c:stats.pf>=1.5?clrGreen:stats.pf>=1?clrAmber:clrRed },
              { l:"Avg R/R", v:String(stats.rr), c:stats.rr>=2?clrGreen:stats.rr>=1?clrAmber:clrRed },
              { l:"Score Fyltra", v:`${stats.score}/100`, c:clrGreen },
            ].map(s => (
              <div key={s.l} style={{ textAlign:"center" }}>
                <div style={{ fontSize:8, color:"rgba(255,255,255,0.35)", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:4 }}>{s.l}</div>
                <div style={{ fontSize:20, fontWeight:700, color:s.c }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* bottom section — instruments + sessions */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, padding:"16px 20px" }}>
        {/* Instruments */}
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, padding:"16px 18px" }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>Par instrument</div>
          {instruments.map(ins => {
            const max = Math.max(...instruments.map(i => Math.abs(i.pnl)));
            return (
              <div key={ins.name} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:9 }}>
                <span style={{ width:54, fontSize:11, color:"rgba(255,255,255,0.6)", letterSpacing:"0.05em" }}>{ins.name}</span>
                <div style={{ flex:1, height:3, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
                  <div style={{ height:"100%", width:`${Math.abs(ins.pnl)/max*100}%`, background:ins.pnl>=0?clrGreen:clrRed, borderRadius:2 }} />
                </div>
                <span style={{ fontSize:11, color:ins.pnl>=0?clrGreen:clrRed, fontWeight:700, minWidth:52, textAlign:"right" }}>{ins.pnl>=0?"+":""}{fmt(ins.pnl)}€</span>
                <span style={{ fontSize:9, color:"rgba(255,255,255,0.3)", minWidth:30, textAlign:"right" }}>{ins.wr}%W</span>
              </div>
            );
          })}
        </div>

        {/* Sessions */}
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, padding:"16px 18px" }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>Sessions</div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={sessions} margin={{ top:0, right:0, left:-30, bottom:0 }}>
              <XAxis dataKey="name" tick={{ fontSize:9, fill:"rgba(255,255,255,0.35)", fontFamily:ff }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize:8, fill:"rgba(255,255,255,0.25)", fontFamily:ff }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background:"#111", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, fontSize:10, fontFamily:ff, color:"#fff" }} />
              <Bar dataKey="pnl" radius={[4,4,0,0]}>
                {sessions.map((s, i) => <Cell key={i} fill={s.pnl>=0?clrGreen:clrRed} fillOpacity={0.8} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// DIRECTION 3 — MAGAZINE
// ──────────────────────────────────────────────
function Magazine() {
  const ff = "'Josefin Sans', sans-serif";
  const bg = "#0c0c0c";
  const card = { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12 };

  const CustomDot = ({ cx, cy, value }) => {
    const last = equity[equity.length - 1].v;
    if (value !== last) return null;
    return <circle cx={cx} cy={cy} r={4} fill={clrGreen} stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} />;
  };

  const GaugeRing = ({ value, max=100, color, size=56, stroke=6, label }) => {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - value / max);
    return (
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition:"stroke-dashoffset 0.8s ease" }} />
        <text x={size/2} y={size/2 + 1} textAnchor="middle" dominantBaseline="middle"
          style={{ transform:"rotate(90deg)", transformOrigin:`${size/2}px ${size/2}px` }}
          fill="#fff" fontSize={size >= 56 ? 13 : 10} fontFamily={ff} fontWeight={700}>{label}</text>
      </svg>
    );
  };

  return (
    <div style={{ background:bg, minHeight:"100vh", fontFamily:ff, color:"#fff" }}>
      <div style={{ display:"grid", gridTemplateColumns:"58% 42%", minHeight:"100vh" }}>

        {/* ── LEFT COLUMN ── */}
        <div style={{ padding:"28px 24px 24px", borderRight:"1px solid rgba(255,255,255,0.06)", display:"flex", flexDirection:"column", gap:14 }}>

          {/* greeting */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase" }}>Samedi 6 juin</div>
              <div style={{ fontSize:22, fontWeight:700, marginTop:3, letterSpacing:"-0.01em" }}>Bonjour, Dany</div>
            </div>
            <div style={{ fontSize:9, color:clrGreen, background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:20, padding:"4px 12px", letterSpacing:"0.1em" }}>
              STREAK {stats.streak}W
            </div>
          </div>

          {/* giant P&L */}
          <div>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:4 }}>Total P&L</div>
            <div style={{ fontSize:88, fontWeight:800, lineHeight:0.9, letterSpacing:"-0.03em", color:clrGreen, textShadow:`0 0 60px ${clrGreen}40` }}>
              +{fmt(stats.pnl)}€
            </div>
            <div style={{ marginTop:10, fontSize:10, color:"rgba(255,255,255,0.3)", letterSpacing:"0.1em" }}>
              {stats.trades} trades &nbsp;·&nbsp; Auj. +{stats.today}€ &nbsp;·&nbsp; Sem. +{fmt(stats.week)}€ &nbsp;·&nbsp; Mois +{fmt(stats.month)}€
            </div>
          </div>

          {/* Equity curve */}
          <div style={{ ...card, padding:"16px 18px", flex:1 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", letterSpacing:"0.2em", textTransform:"uppercase" }}>Equity curve — 30J</div>
              <div style={{ fontSize:18, fontWeight:700, color:clrGreen }}>+{fmt(equity[equity.length-1].v - equity[0].v)}€</div>
            </div>
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={equity} margin={{ top:4, right:0, left:-36, bottom:0 }}>
                <defs>
                  <linearGradient id="mg3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={clrGreen} stopOpacity={0.3}/>
                    <stop offset="100%" stopColor={clrGreen} stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="d" tick={{ fontSize:8, fill:"rgba(255,255,255,0.25)", fontFamily:ff }} tickLine={false} axisLine={false} interval={4} />
                <YAxis tick={{ fontSize:8, fill:"rgba(255,255,255,0.2)", fontFamily:ff }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background:"#111", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, fontSize:10, fontFamily:ff, color:"#fff" }} />
                <Area type="monotone" dataKey="v" stroke={clrGreen} strokeWidth={2} fill="url(#mg3)" dot={<CustomDot />} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* instruments */}
          <div style={{ ...card, padding:"14px 18px" }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>Par instrument</div>
            <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
              {instruments.map(ins => {
                const max = Math.max(...instruments.map(i => Math.abs(i.pnl)));
                return (
                  <div key={ins.name} style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ width:58, fontSize:10, color:"rgba(255,255,255,0.55)", letterSpacing:"0.05em" }}>{ins.name}</span>
                    <div style={{ flex:1, height:3, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
                      <div style={{ height:"100%", width:`${Math.abs(ins.pnl)/max*100}%`, background:ins.pnl>=0?clrGreen:clrRed, borderRadius:2 }} />
                    </div>
                    <span style={{ fontSize:11, color:ins.pnl>=0?clrGreen:clrRed, fontWeight:700, minWidth:56, textAlign:"right" }}>{ins.pnl>=0?"+":""}{fmt(ins.pnl)}€</span>
                    <span style={{ fontSize:9, color:"rgba(255,255,255,0.28)", minWidth:30, textAlign:"right" }}>{ins.wr}%W</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div style={{ padding:"28px 20px 24px", display:"flex", flexDirection:"column", gap:10 }}>

          {/* Score Fyltra */}
          <div style={{ ...card, padding:"20px", display:"flex", gap:20, alignItems:"center" }}>
            <GaugeRing value={stats.score} max={100} color={clrGreen} size={72} stroke={7} label={`${stats.score}`} />
            <div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:5 }}>Score Fyltra</div>
              <div style={{ fontSize:22, fontWeight:700, color:clrGreen }}>Excellent</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:3 }}>Top 12% des traders</div>
            </div>
          </div>

          {/* 3 KPI rings */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
            {[
              { l:"Win Rate", v:stats.wr, max:100, suf:"%", c:stats.wr>=60?clrGreen:stats.wr>=45?clrAmber:clrRed },
              { l:"Profit Factor", v:Math.min(stats.pf, 5)*20, max:100, suf:"", raw:stats.pf, c:stats.pf>=1.5?clrGreen:stats.pf>=1?clrAmber:clrRed },
              { l:"Avg R/R", v:Math.min(stats.rr, 5)*20, max:100, suf:"", raw:stats.rr, c:stats.rr>=2?clrGreen:stats.rr>=1?clrAmber:clrRed },
            ].map(k => (
              <div key={k.l} style={{ ...card, padding:"14px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
                <div style={{ fontSize:8, color:"rgba(255,255,255,0.3)", letterSpacing:"0.15em", textTransform:"uppercase", textAlign:"center" }}>{k.l}</div>
                <GaugeRing value={k.v} max={100} color={k.c} size={54} stroke={5} label={k.raw != null ? String(k.raw) : `${k.v}${k.suf}`} />
              </div>
            ))}
          </div>

          {/* Today / Week / Month */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
            {[
              { l:"Auj.", v:stats.today },
              { l:"Semaine", v:stats.week },
              { l:"Mois", v:stats.month },
            ].map(m => (
              <div key={m.l} style={{ ...card, padding:"14px 12px" }}>
                <div style={{ fontSize:8, color:"rgba(255,255,255,0.3)", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:6 }}>{m.l}</div>
                <div style={{ fontSize:20, fontWeight:700, color:m.v>=0?clrGreen:clrRed, letterSpacing:"-0.01em" }}>{m.v>=0?"+":""}{fmt(m.v)}€</div>
              </div>
            ))}
          </div>

          {/* Sessions bar */}
          <div style={{ ...card, padding:"16px 18px", flex:1 }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>Sessions</div>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={sessions} margin={{ top:0, right:0, left:-30, bottom:0 }}>
                <XAxis dataKey="name" tick={{ fontSize:9, fill:"rgba(255,255,255,0.3)", fontFamily:ff }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize:8, fill:"rgba(255,255,255,0.2)", fontFamily:ff }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background:"#111", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, fontSize:10, fontFamily:ff, color:"#fff" }} />
                <Bar dataKey="pnl" radius={[4,4,0,0]}>
                  {sessions.map((s, i) => <Cell key={i} fill={s.pnl>=0?clrGreen:clrRed} fillOpacity={0.75} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Drawdown compact */}
          <div style={{ ...card, padding:"14px 18px" }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>Drawdown 24H</div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontSize:26, fontWeight:700, color:clrGreen }}>0%</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>/ 500€ limite</div>
            </div>
            <div style={{ marginTop:8, height:3, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
              <div style={{ height:"100%", width:"0%", background:clrGreen, borderRadius:2 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// DIRECTION 4 — MIX 2+3
// ──────────────────────────────────────────────
function Mix() {
  const ff = "'Josefin Sans', sans-serif";
  const bg = "#0c0c0c";
  const card = { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12 };
  const hc = clrGreen;

  const GaugeRing = ({ value, max=100, color, size=56, stroke=6, label }) => {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - Math.min(value, max) / max);
    return (
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition:"stroke-dashoffset 0.8s ease" }} />
        <text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle"
          style={{ transform:`rotate(90deg)`, transformOrigin:`${size/2}px ${size/2}px` }}
          fill="#fff" fontSize={size>=72?15:size>=54?12:10} fontFamily={ff} fontWeight="700">{label}</text>
      </svg>
    );
  };

  return (
    <div style={{ background:bg, minHeight:"100vh", fontFamily:ff, color:"#fff", display:"grid", gridTemplateColumns:"58% 42%" }}>

      {/* ── COLONNE GAUCHE — zone graphique immersive ── */}
      <div style={{ position:"relative", borderRight:"1px solid rgba(255,255,255,0.07)", display:"flex", flexDirection:"column" }}>

        {/* Equity curve plein fond */}
        <div style={{ position:"absolute", inset:0, zIndex:0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={equity} margin={{ top:0, right:0, left:0, bottom:0 }}>
              <defs>
                <linearGradient id="mix4grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={hc} stopOpacity={0.22}/>
                  <stop offset="70%" stopColor={hc} stopOpacity={0.04}/>
                  <stop offset="100%" stopColor={hc} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={hc} strokeWidth={2} fill="url(#mix4grad)" dot={false} strokeOpacity={0.7} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Overlays sur le graphique */}
        <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", height:"100%", padding:"28px 28px 0" }}>

          {/* Greeting */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", letterSpacing:"0.2em", textTransform:"uppercase" }}>Samedi 7 juin</div>
              <div style={{ fontSize:22, fontWeight:700, marginTop:3, letterSpacing:"-0.01em" }}>Bonjour, Dany</div>
            </div>
            <div style={{ fontSize:9, color:"#fcd34d", background:"rgba(252,211,77,0.1)", border:"1px solid rgba(252,211,77,0.2)", borderRadius:20, padding:"5px 14px", letterSpacing:"0.1em" }}>
              STREAK {stats.streak}W
            </div>
          </div>

          {/* P&L flottant — centre */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center" }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.25em", textTransform:"uppercase", marginBottom:8 }}>Total P&L</div>
            <div style={{ fontSize:96, fontWeight:800, lineHeight:0.9, letterSpacing:"-0.04em", color:hc,
              textShadow:`0 0 80px ${hc}55, 0 0 30px ${hc}30` }}>
              +{fmt(stats.pnl)}€
            </div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:14, letterSpacing:"0.1em" }}>
              {stats.trades} trades &nbsp;·&nbsp; Auj. +{stats.today}€
            </div>
          </div>

          {/* Instruments en bas — fond flouté */}
          <div style={{ background:"rgba(12,12,12,0.75)", backdropFilter:"blur(20px)", borderTop:"1px solid rgba(255,255,255,0.07)", padding:"16px 0 20px" }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.28)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>Par instrument</div>
            {instruments.map(ins => {
              const max = Math.max(...instruments.map(i => Math.abs(i.pnl)));
              return (
                <div key={ins.name} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                  <span style={{ width:58, fontSize:10, color:"rgba(255,255,255,0.55)" }}>{ins.name}</span>
                  <div style={{ flex:1, height:2, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
                    <div style={{ height:"100%", width:`${Math.abs(ins.pnl)/max*100}%`, background:ins.pnl>=0?clrGreen:clrRed, borderRadius:2 }} />
                  </div>
                  <span style={{ fontSize:11, color:ins.pnl>=0?clrGreen:clrRed, fontWeight:700, minWidth:56, textAlign:"right" }}>{ins.pnl>=0?"+":""}{fmt(ins.pnl)}€</span>
                  <span style={{ fontSize:9, color:"rgba(255,255,255,0.28)", minWidth:30, textAlign:"right" }}>{ins.wr}%W</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── COLONNE DROITE — stats ── */}
      <div style={{ display:"flex", flexDirection:"column", gap:10, padding:"28px 20px 24px", overflowY:"auto" }}>

        {/* Score */}
        <div style={{ ...card, padding:"18px 20px", display:"flex", alignItems:"center", gap:16 }}>
          <GaugeRing value={stats.score} max={100} color={clrGreen} size={76} stroke={7} label={String(stats.score)} />
          <div>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:5 }}>Score Fyltra</div>
            <div style={{ fontSize:18, fontWeight:700, color:clrGreen }}>Excellent</div>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.28)", marginTop:3 }}>Top 12% des traders</div>
          </div>
        </div>

        {/* 3 KPI rings */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
          {[
            { l:"Win Rate",      v:stats.wr,              max:100, c:stats.wr>=60?clrGreen:stats.wr>=45?clrAmber:clrRed, txt:`${stats.wr}%`,  note:"7W / 5L" },
            { l:"Profit Factor", v:Math.min(stats.pf*20,100), max:100, c:stats.pf>=1.5?clrGreen:stats.pf>=1?clrAmber:clrRed, txt:String(stats.pf), note:"+3.2k / -1.5k" },
            { l:"Avg R/R",       v:Math.min(stats.rr/3*100,100), max:100, c:stats.rr>=2?clrGreen:stats.rr>=1?clrAmber:clrRed, txt:String(stats.rr), note:"7 trades" },
          ].map(k => (
            <div key={k.l} style={{ ...card, padding:"12px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
              <div style={{ fontSize:8, color:"rgba(255,255,255,0.3)", letterSpacing:"0.12em", textTransform:"uppercase", textAlign:"center" }}>{k.l}</div>
              <GaugeRing value={k.v} max={100} color={k.c} size={54} stroke={5} label={k.txt} />
              <div style={{ fontSize:8, color:"rgba(255,255,255,0.28)", textAlign:"center" }}>{k.note}</div>
            </div>
          ))}
        </div>

        {/* Auj / Sem / Mois */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
          {[
            { l:"Auj.",    v:stats.today },
            { l:"Semaine", v:stats.week },
            { l:"Mois",    v:stats.month },
          ].map(m => (
            <div key={m.l} style={{ ...card, padding:"12px 14px" }}>
              <div style={{ fontSize:8, color:"rgba(255,255,255,0.3)", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:5 }}>{m.l}</div>
              <div style={{ fontSize:18, fontWeight:700, color:m.v>=0?clrGreen:clrRed, letterSpacing:"-0.01em" }}>
                {m.v>=0?"+":""}{fmt(m.v)}€
              </div>
            </div>
          ))}
        </div>

        {/* Sessions */}
        <div style={{ ...card, padding:"14px 16px" }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:4 }}>Sessions</div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={sessions} margin={{ top:8, right:8, bottom:0, left:8 }}>
              <XAxis dataKey="name" tick={{ fontSize:9, fill:"rgba(255,255,255,0.3)", fontFamily:ff }} tickLine={false} axisLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={{ background:"#111", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, fontSize:10, fontFamily:ff, color:"#fff" }} />
              <Bar dataKey="pnl" radius={[4,4,0,0]}>
                {sessions.map((s,i) => <Cell key={i} fill={s.pnl>=0?clrGreen:clrRed} fillOpacity={0.8} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Drawdown */}
        <div style={{ ...card, padding:"14px 18px" }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>Drawdown 24H</div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <div style={{ fontSize:28, fontWeight:700, color:clrGreen }}>0%</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.28)" }}>/ 500€ limite</div>
          </div>
          <div style={{ height:3, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
            <div style={{ height:"100%", width:"0%", background:clrGreen, borderRadius:2 }} />
          </div>
        </div>

      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// SWITCHER
// ──────────────────────────────────────────────
const DIRS = [
  { key:"terminal",  label:"1 — Terminal" },
  { key:"hero",      label:"2 — Hero Graphique" },
  { key:"magazine",  label:"3 — Magazine" },
  { key:"mix",       label:"4 — Mix 2+3" },
];

export default function DashboardPreview() {
  const [active, setActive] = useState("terminal");

  return (
    <div>
      {/* switcher bar */}
      <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:9999, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(12px)", borderBottom:"1px solid rgba(255,255,255,0.1)", display:"flex", gap:0, alignItems:"center", padding:"0 20px", height:44 }}>
        <span style={{ fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginRight:16 }}>PREVIEW</span>
        {DIRS.map(d => (
          <button key={d.key} onClick={() => setActive(d.key)}
            style={{ padding:"0 20px", height:"100%", border:"none", background:"transparent", color:active===d.key?"#fff":"rgba(255,255,255,0.35)", fontSize:11, fontFamily:"'Josefin Sans',sans-serif", fontWeight:active===d.key?700:400, letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer", borderBottom:active===d.key?"2px solid #22c55e":"2px solid transparent", transition:"all 0.2s" }}>
            {d.label}
          </button>
        ))}
      </div>
      {/* content with top padding */}
      <div style={{ paddingTop:44 }}>
        {active === "terminal" && <Terminal />}
        {active === "hero"     && <HeroGraph />}
        {active === "magazine" && <Magazine />}
        {active === "mix"      && <Mix />}
      </div>
    </div>
  );
}
